import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { DarkMode, withStyles } from '@glu/theming';
import locale from '@glu/locale';
import {
  ColumnsNextIcon,
  ContainerArrowDownNextIcon,
  FunnelNextIcon,
  PrinterNextIcon,
  ArrowsCirclesIcon
} from '@glu/icons-react';
import { SavedViews } from '@glu/data-components';
import SearchInput from './SearchInput';
import { styles } from './ControlBar.styles';
import Drawer from './Drawer';
import exportGrid from '../../utils/export';
import { showFilters, showColumns, showDivider } from './ControlBars.util';

export function ControlBarComponent(props) {
  const {
    MultiselectFilterProps,
    PopoverProps,
    TypeaheadFilterProps,
    classes,
    comparisonConvertor,
    customControlBarContent,
    customPrint,
    data,
    domLayout,
    errors,
    fetchSavedViews,
    gridApi,
    gridId,
    hideColumns,
    hideDrawerMenu,
    hideExport,
    hideFilters,
    hidePrint,
    initialData,
    lastSync,
    numberFilterTypes,
    onChange,
    onChangeAsync,
    onExportToCsv,
    onExportToExcel,
    onLoadError,
    onSave,
    onSaveError,
    onSavedViewsLoad,
    onSearchClear,
    onSearchSubmit,
    records,
    saveView,
    savedViewsMaxCount,
    savedViewsSuggestedName,
    searchEnabled,
    showControlBar,
    showExportMenu,
    showSavedViews,
    syncData,
    validate
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [tab, setTab] = useState('columns');
  const [exportMenuVisible, setExportMenuVisible] = useState(false);
  const exportListRef = useRef(null);
  const exportIconRef = useRef(null);
  const displayProps = {
    hideColumns,
    hideDrawerMenu,
    hideExport,
    hideFilters,
    hidePrint
  };
  const handleDrawerToggle = () => {
    setMenuOpen((current) => !current);
  };

  const handleExportIconClick = () => {
    if (showExportMenu) {
      setExportMenuVisible(!exportMenuVisible);
    } else {
      exportGrid(props);
    }
  };

  // DOM click testing impractical with unit tests
  /* istanbul ignore next */
  useEffect(() => {
    function handleOutsideExportMenuClick(event) {
      if (!exportListRef.current?.contains(event.target)
        && !exportIconRef.current?.contains(event.target)) {
        setExportMenuVisible(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideExportMenuClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideExportMenuClick);
    };
  }, [exportListRef, exportIconRef]);

  const handlePrint = () => {
    if (!gridApi) {
      return;
    }
    gridApi.setDomLayout('print');
    setTimeout(() => {
      window.print();
      gridApi.setDomLayout(domLayout);
    }, 1000);
  };

  return (
    <>
      { showControlBar && (
        <div className={classes.controlBar} data-qa="controlBar">
          <div className={classes.preControls}>
            {showSavedViews
              && (
                <div data-qa="savedViews-wrapper">
                  <SavedViews
                    comparisonConvertor={comparisonConvertor}
                    errors={errors}
                    fetchSavedViews={fetchSavedViews}
                    initialData={initialData}
                    onChangeAsync={onChangeAsync}
                    onLoadError={onLoadError}
                    onSave={onSave}
                    onSaveError={onSaveError}
                    PopoverProps={PopoverProps}
                    saveView={saveView}
                    validate={validate}
                    onLoad={onSavedViewsLoad}
                    hasDefaultState
                    id={gridId}
                    data={data}
                    name="savedViews"
                    onChange={onChange}
                    maxViewsCount={savedViewsMaxCount}
                    suggestedName={savedViewsSuggestedName}
                  />
                </div>
              )}
          </div>
          <div className={classes.controls}>
            {searchEnabled && (
              <SearchInput
                onClear={onSearchClear}
                onSearch={onSearchSubmit}
                placeholder={locale.get('gridReact.search.placeholder')}
              />
            )}
            {(syncData || lastSync) ? (
              <div className={classes.lastSyncWrapper} title={locale.get('gridReact.lastSyncLabel')}>
                {syncData && (
                <ArrowsCirclesIcon
                  className={classes.lastSyncIcon}
                  onClick={syncData}
                  data-qa="arrow-circles-icon"
                />
                )}
                {lastSync && (
                <div className={classes.syncText} data-qa="last-sync-text">
                  {locale.get('gridReact.lastSync', lastSync)}
                </div>
                )}
              </div>
            ) : null}
            {showFilters(displayProps) && (
            <div title={locale.get('gridReact.filters')}>
              <FunnelNextIcon
                title={locale.get('gridReact.filters')}
                className={classes.controlIcon}
                data-qa="filterToggle"
                onClick={() => {
                  handleDrawerToggle();
                  setTab('filters');
                }}
                actionable
              />
            </div>
            )}
            {showColumns(displayProps) && (
            <div title={locale.get('gridReact.columns')}>
              <ColumnsNextIcon
                title={locale.get('gridReact.columns')}
                className={classes.controlIcon}
                data-qa="columnsToggle"
                onClick={() => {
                  handleDrawerToggle();
                  setTab('columns');
                }}
                actionable
              />
            </div>
            )}
            {showDivider(displayProps) && (
            <div className={classes.divider} />
            )}
            <div className={classes.controlGroup}>
              {(!hidePrint && !customPrint) && (
              <div title={locale.get('gridReact.print')}>
                <PrinterNextIcon
                  title={locale.get('gridReact.print')}
                  className={classes.controlIcon}
                  data-qa="printButton"
                  onClick={handlePrint}
                  actionable
                />
              </div>
              )}
              {(!hidePrint && customPrint) && customPrint(gridApi)}
              {!hideExport
                && (
                  <div title={locale.get('gridReact.export')} ref={exportIconRef} className={classes.exportMenuContainer}>
                    <ContainerArrowDownNextIcon
                      title={locale.get('gridReact.export')}
                      data-qa="exportButton"
                      className={classes.controlIcon}
                      onClick={() => handleExportIconClick()}
                      actionable
                    />
                    {exportMenuVisible && (
                      <ul data-qa="exportMenu" ref={exportListRef} className={classes.exportMenu}>
                        <li
                          role="presentation"
                          data-qa="exportToCsv"
                          onClick={() => {
                            setExportMenuVisible(!exportMenuVisible);
                            if (onExportToCsv) {
                              onExportToCsv(props);
                            } else {
                              exportGrid(props);
                            }
                          }}
                        >
                          {locale.get('gridReact.downloadCsv')}
                        </li>
                        <li
                          role="presentation"
                          data-qa="exportToExcel"
                          onClick={() => {
                            setExportMenuVisible(!exportMenuVisible);
                            if (onExportToExcel) {
                              onExportToExcel(props);
                            }
                          }}
                        >
                          {locale.get('gridReact.downloadExcel')}
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              {customControlBarContent}
            </div>
          </div>
        </div>
      )}
      <DarkMode>
        <Drawer
          activeTab={tab}
          data={data}
          hideColumns={hideColumns}
          hideFilters={hideFilters}
          id={gridId}
          menuOpen={menuOpen}
          onChange={onChange}
          onDrawerToggle={handleDrawerToggle}
          onTabClick={setTab}
          setMenuOpen={setMenuOpen}
          numberFilterTypes={numberFilterTypes}
          records={records}
          MultiselectFilterProps={MultiselectFilterProps}
          TypeaheadFilterProps={TypeaheadFilterProps}
        />
      </DarkMode>
    </>
  );
}

ControlBarComponent.propTypes = {

  MultiselectFilterProps: PropTypes.shape({}),

  /** Options for @popperjs/core see docs for details */
  PopoverProps: PropTypes.shape({}),

  TypeaheadFilterProps: PropTypes.shape({}),

  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /**
   * Function to convert data object before comparison
   * Can be used to whitelist or blacklist fields.
   */
  comparisonConvertor: PropTypes.func,

  customControlBarContent: PropTypes.node,
  customPrint: PropTypes.func,

  data: PropTypes.shape({
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string
      })
    ),
    filters: PropTypes.arrayOf(PropTypes.shape({})),
    grid: PropTypes.shape({}),
    page: PropTypes.number,
    pageSize: PropTypes.number,
    savedViews: PropTypes.shape({}),
    sort: PropTypes.arrayOf(
      PropTypes.shape({
        colId: PropTypes.string,
        sort: PropTypes.oneOf(['asc', 'desc'])
      })
    )
  }).isRequired,

  domLayout: PropTypes.oneOf(['autoHeight', 'normal', 'print']),

  errors: PropTypes.objectOf(PropTypes.string),

  /** Fetch function must return a promise */
  fetchSavedViews: PropTypes.func,

  gridApi: PropTypes.shape({ setDomLayout: PropTypes.func }),

  gridId: PropTypes.string.isRequired,

  hideColumns: PropTypes.bool,

  /** Hide display of the drawer containing both filters and columns */
  hideDrawerMenu: PropTypes.bool,

  hideExport: PropTypes.bool,

  hideFilters: PropTypes.bool,

  hidePrint: PropTypes.bool,

  /** Initial data. Used to detect changes before a saved view is loaded */
  initialData: PropTypes.shape({
    savedViews: PropTypes.shape({
      views: PropTypes.arrayOf(PropTypes.shape({
        viewData: PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          isDefault: PropTypes.bool,
          name: PropTypes.string
        })
      }))
    })
  }),

  lastSync: PropTypes.string,

  numberFilterTypes: PropTypes.arrayOf(
    PropTypes.shape({ text: PropTypes.string, value: PropTypes.string })
  ),

  onChange: PropTypes.func.isRequired,

  /** if onChange returns a promise or is asynchronous itself pass */
  onChangeAsync: PropTypes.bool,

  /** Function to be called when export to csv is clicked */
  onExportToCsv: PropTypes.func,

  /** Function to be called when export to excel is clicked */
  onExportToExcel: PropTypes.func,

  /** Called if fetchSavedViews errors */
  onLoadError: PropTypes.func,

  /** Called after saveView returns */
  onSave: PropTypes.func,

  /** Called if saveView errors */
  onSaveError: PropTypes.func,

  onSavedViewsLoad: PropTypes.func,

  onSearchClear: PropTypes.func,

  onSearchSubmit: PropTypes.func,

  records: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  /** Function for saving. Must return a promise */
  saveView: PropTypes.func,

  savedViewsMaxCount: PropTypes.number,

  savedViewsSuggestedName: PropTypes.string,

  searchEnabled: PropTypes.bool,

  showControlBar: PropTypes.bool,

  /** Show export menu for CSV and Excel */
  showExportMenu: PropTypes.bool,

  showSavedViews: PropTypes.bool.isRequired,

  syncData: PropTypes.func,
  /** Validation function for naming a saved view */
  validate: PropTypes.func
};

/* istanbul ignore next */
const saveToLocalStorage = ({ id, newViews }) => Promise.resolve()
  .then(
    () => localStorage
      .setItem(id, JSON.stringify(newViews))
  );

ControlBarComponent.defaultProps = {
  MultiselectFilterProps: undefined,
  PopoverProps: undefined,
  TypeaheadFilterProps: undefined,
  comparisonConvertor: null,
  customControlBarContent: undefined,
  customPrint: null,
  domLayout: 'autoHeight',
  errors: {},
  fetchSavedViews: undefined,
  gridApi: null,
  hideColumns: false,
  hideDrawerMenu: false,
  hideExport: false,
  hideFilters: false,
  hidePrint: false,
  initialData: null,
  lastSync: '',
  numberFilterTypes: undefined,
  onChangeAsync: false,
  onExportToCsv: null,
  onExportToExcel: null,
  onLoadError: /* istanbul ignore next */ () => { },
  onSave: /* istanbul ignore next */ () => { },
  onSaveError: /* istanbul ignore next */ () => { },
  onSavedViewsLoad: /* istanbul ignore next */ () => { },
  onSearchClear: undefined,
  onSearchSubmit: undefined,
  saveView: saveToLocalStorage,
  savedViewsMaxCount: undefined,
  savedViewsSuggestedName: '',
  searchEnabled: false,
  showControlBar: true,
  showExportMenu: false,
  syncData: null,
  validate: null
};

export default withStyles(styles)(ControlBarComponent);
